/* Timed lyrics for the app's own practice songs (covers never carry lyrics).
   A line starts on the downbeat of a bar and lists its words with their length in beats:
     { bar: 5, text: 'Elimde:2 gitar:2 ilk:1 akor:3' }
   A word without ":n" lasts one beat, "-" (or "-:2") is a rest, so a line can start after the downbeat. */

export interface LyricLine {
  /** Bar the line starts on, counted from 1 as on the chart. */
  bar: number;
  /** Words with their length in beats: "Elimde:2 gitar:2 ilk:1 akor:3". */
  text: string;
}

export interface LyricToken {
  /** null for a rest */
  word: string | null;
  beats: number;
}

export interface TimedWord {
  text: string;
  /** seconds from the song's first downbeat */
  t: number;
  end: number;
}

export interface TimedLine {
  t: number;
  end: number;
  words: TimedWord[];
}

const TOKEN = /^(.*?)(?::(\d+(?:\.\d+)?))?$/;

/** Splits a line into words and rests; null when a token is malformed. */
export function parseLyric(text: string): LyricToken[] | null {
  const out: LyricToken[] = [];
  for (const tok of text.trim().split(/\s+/)) {
    const m = TOKEN.exec(tok);
    const word = m?.[1] ?? '';
    const beats = m?.[2] ? Number.parseFloat(m[2]) : 1;
    if (!word || !(beats > 0)) return null;
    out.push({ word: word === '-' ? null : word, beats });
  }
  return out.some((t) => t.word) ? out : null;
}

/** Length of a line in beats, rests included (0 when it does not parse). */
export const lyricBeats = (text: string): number => (parseLyric(text) ?? []).reduce((n, t) => n + t.beats, 0);

/** Lines with word times in seconds, for a tempo of `beat` seconds per beat and `beatsPerBar`. */
export function timeLyrics(lines: readonly LyricLine[], beat: number, beatsPerBar: number): TimedLine[] {
  const out: TimedLine[] = [];
  for (const line of lines) {
    const tokens = parseLyric(line.text);
    if (!tokens) continue;
    let t = (line.bar - 1) * beatsPerBar * beat;
    const words: TimedWord[] = [];
    for (const tok of tokens) {
      const end = t + tok.beats * beat;
      if (tok.word) words.push({ text: tok.word, t, end });
      t = end;
    }
    out.push({ t: (words[0] as TimedWord).t, end: (words.at(-1) as TimedWord).end, words });
  }
  return out;
}
